import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { User, Mail, Phone, Lock, Gift, ArrowRight, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';

function formatError(detail) {
  if (!detail) return 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Read referral code from URL ?ref=CODE
  const urlParams = new URLSearchParams(window.location.search);
  const refFromUrl = urlParams.get('ref') || '';

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ name: '', email: '', mobile: '', password: '', referral_code: refFromUrl });
  const [forgotForm, setForgotForm] = useState({ contact_info: '', contact_type: 'whatsapp' });
  const [showLoginPass, setShowLoginPass] = useState(false);
  const [showRegPass, setShowRegPass] = useState(false);

  // Auto-switch to register tab if referral code is present
  useEffect(() => {
    if (refFromUrl) {
      setTab('register');
      // Store for Google Auth flow
      localStorage.setItem('pending_referral', refFromUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch helpline settings for login page
  const [helpline, setHelpline] = useState(null);
  useEffect(() => {
    api.get('/settings/helpline-public').then(r => setHelpline(r.data)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(loginForm.email, loginForm.password);
      toast.success('Login successful!');
      const role = data.user.role;
      navigate(role === 'admin' ? '/admin' : role === 'sub_admin' ? '/subadmin' : '/');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(regForm);
      toast.success('Registration successful!');
      navigate('/');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const api = (await import('../lib/api')).default;
      await api.post('/auth/forgot-password', forgotForm);
      toast.success('Request sent to admin. You will be contacted soon.');
      setShowForgot(false);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
        style={{ background: `linear-gradient(135deg, rgba(10,10,14,0.95), rgba(15,5,25,0.98))` }}>
        <div className="glass-card rounded-2xl p-8 w-full max-w-md fade-in-up">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
            Forgot Password
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--neon-text-secondary)' }}>
            Enter your WhatsApp number or email. Admin will send you a reset link.
          </p>
          <form onSubmit={handleForgot} className="space-y-4">
            <div className="flex gap-3 mb-4">
              <button type="button" data-testid="forgot-type-whatsapp"
                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${forgotForm.contact_type === 'whatsapp' ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
                onClick={() => setForgotForm(f => ({ ...f, contact_type: 'whatsapp' }))}>
                Mobile Number
              </button>
              <button type="button" data-testid="forgot-type-email"
                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${forgotForm.contact_type === 'email' ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
                onClick={() => setForgotForm(f => ({ ...f, contact_type: 'email' }))}>
                Email
              </button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
                {forgotForm.contact_type === 'whatsapp' ? 'Mobile Number' : 'Email Address'}
              </Label>
              <Input data-testid="forgot-contact-input" className="neon-input" required
                placeholder={forgotForm.contact_type === 'whatsapp' ? 'XXXXXXXXXX' : 'your@email.com'}
                value={forgotForm.contact_info}
                onChange={(e) => setForgotForm(f => ({ ...f, contact_info: e.target.value }))} />
            </div>
            <button type="submit" data-testid="forgot-submit-btn" disabled={loading}
              className="w-full btn-neon-primary disabled:opacity-50">
              {loading ? 'Sending...' : 'Send Request'}
            </button>
            <button type="button" data-testid="forgot-back-btn"
              className="w-full text-sm mt-2" style={{ color: 'var(--neon-text-secondary)' }}
              onClick={() => setShowForgot(false)}>
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ background: `linear-gradient(135deg, rgba(10,10,14,0.95), rgba(15,5,25,0.98))` }}>

      {/* Helpline Section - Top */}
      {helpline && (helpline.whatsapp_number || helpline.telegram_link) && (
        <div className="helpline-blink flex items-center justify-center gap-8 mb-5 w-full max-w-md">
          {helpline.telegram_link && (
            <a href={(() => {
              let link = helpline.telegram_link.trim();
              if (!link.includes('://')) link = link.replace('http//', 'https://').replace('https//', 'https://');
              if (!link.startsWith('http')) link = 'https://' + link;
              return link;
            })()} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 transition-all hover:scale-110">
              <div className="w-12 h-12 rounded-full flex items-center justify-center helpline-icon-pulse"
                style={{ background: 'rgba(0,136,204,0.15)', border: '2px solid rgba(0,136,204,0.4)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#0088CC">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                </svg>
              </div>
              <span className="text-xs font-bold" style={{ color: '#0088CC' }}>Telegram Help</span>
            </a>
          )}
          {helpline.whatsapp_number && (
            <a href={`https://wa.me/${helpline.whatsapp_number.replace(/[^0-9]/g, '')}?text=Help%20needed`} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 transition-all hover:scale-110">
              <div className="w-12 h-12 rounded-full flex items-center justify-center helpline-icon-pulse"
                style={{ background: 'rgba(37,211,102,0.15)', border: '2px solid rgba(37,211,102,0.4)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <span className="text-xs font-bold" style={{ color: '#25D366' }}>WhatsApp Help</span>
            </a>
          )}
        </div>
      )}

      {/* Login Card */}
      <div className="glass-card rounded-2xl p-8 w-full max-w-md fade-in-up">
        <h1 className="text-4xl font-black text-center mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
          <span className="brand-glow" style={{ fontFamily: 'var(--font-heading)' }}>Mukamari</span>
        </h1>
        <p className="text-center text-sm mb-6" style={{ color: 'var(--neon-text-secondary)' }}>
          Gaming & Betting Platform
        </p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 mb-6 rounded-lg p-1" style={{ background: 'var(--neon-elevated)' }}>
            <TabsTrigger data-testid="login-tab" value="login"
              className="rounded-md text-sm font-bold data-[state=active]:text-black"
              style={{ fontFamily: 'var(--font-heading)' }}>
              Login
            </TabsTrigger>
            <TabsTrigger data-testid="register-tab" value="register"
              className="rounded-md text-sm font-bold data-[state=active]:text-black"
              style={{ fontFamily: 'var(--font-heading)' }}>
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
                  <Mail size={12} className="inline mr-1" /> Email or Mobile
                </Label>
                <Input data-testid="login-email-input" className="neon-input" required
                  placeholder="email@example.com or mobile"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
                  <Lock size={12} className="inline mr-1" /> Password
                </Label>
                <div className="relative">
                  <Input data-testid="login-password-input" className="neon-input pr-10" type={showLoginPass ? "text" : "password"} required
                    placeholder="Enter password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm(f => ({ ...f, password: e.target.value }))} />
                  <button type="button" data-testid="login-toggle-pass" className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--neon-text-muted)' }}
                    onClick={() => setShowLoginPass(!showLoginPass)}>
                    {showLoginPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" data-testid="login-submit-btn" disabled={loading}
                className="w-full btn-neon-primary flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? 'Logging in...' : <><span>Login</span><ArrowRight size={16} /></>}
              </button>
              <button type="button" data-testid="forgot-password-btn"
                className="w-full text-sm mt-1" style={{ color: 'var(--neon-secondary)' }}
                onClick={() => setShowForgot(true)}>
                Forgot Password?
              </button>

              {/* Google OAuth Divider */}
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
                <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>OR</span>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
              </div>

              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <button type="button" data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-3 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] active:scale-95"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--neon-text)' }}
                onClick={() => {
                  // Save referral code for Google Auth flow
                  if (regForm.referral_code) localStorage.setItem('pending_referral', regForm.referral_code);
                  const redirectUrl = window.location.origin + '/';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>
                  <User size={12} className="inline mr-1" /> Name
                </Label>
                <Input data-testid="register-name-input" className="neon-input" required
                  placeholder="Your name" value={regForm.name}
                  onChange={(e) => setRegForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>
                  <Mail size={12} className="inline mr-1" /> Email
                </Label>
                <Input data-testid="register-email-input" className="neon-input" required type="email"
                  placeholder="email@example.com" value={regForm.email}
                  onChange={(e) => setRegForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>
                  <Phone size={12} className="inline mr-1" /> Mobile
                </Label>
                <Input data-testid="register-mobile-input" className="neon-input"
                  placeholder="+91 XXXXXXXXXX" value={regForm.mobile}
                  onChange={(e) => setRegForm(f => ({ ...f, mobile: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>
                  <Lock size={12} className="inline mr-1" /> Password
                </Label>
                <div className="relative">
                  <Input data-testid="register-password-input" className="neon-input pr-10" type={showRegPass ? "text" : "password"} required
                    placeholder="Min 6 characters" value={regForm.password}
                    onChange={(e) => setRegForm(f => ({ ...f, password: e.target.value }))} />
                  <button type="button" data-testid="register-toggle-pass" className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--neon-text-muted)' }}
                    onClick={() => setShowRegPass(!showRegPass)}>
                    {showRegPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: refFromUrl ? '#01FFC3' : 'var(--neon-text-muted)' }}>
                  <Gift size={12} className="inline mr-1" /> Referral Code {refFromUrl ? '(Auto-Applied)' : '(Optional)'}
                </Label>
                <Input data-testid="register-referral-input" className="neon-input"
                  placeholder="Enter referral code" value={regForm.referral_code}
                  readOnly={!!refFromUrl}
                  style={refFromUrl ? { borderColor: '#01FFC3', color: '#01FFC3' } : {}}
                  onChange={(e) => setRegForm(f => ({ ...f, referral_code: e.target.value }))} />
              </div>
              <button type="submit" data-testid="register-submit-btn" disabled={loading}
                className="w-full btn-neon-primary flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? 'Creating...' : <><span>Create Account</span><ArrowRight size={16} /></>}
              </button>

              {/* Google OAuth Divider */}
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
                <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>OR QUICK REGISTER</span>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
              </div>

              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <button type="button" data-testid="google-register-btn"
                className="w-full flex items-center justify-center gap-3 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] active:scale-95"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--neon-text)' }}
                onClick={() => {
                  if (regForm.referral_code) localStorage.setItem('pending_referral', regForm.referral_code);
                  const redirectUrl = window.location.origin + '/';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
